<template>
  <Transition name="resume-playback">
    <div
      v-if="visible"
      class="resume-playback"
      role="status"
      aria-live="polite"
    >
      <button
        class="resume-playback__action"
        :aria-label="resumeLabel"
        @click.stop="handleResume"
      >
        <svg class="resume-playback__icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
          <path :d="mdiPlay" />
        </svg>
        <span class="resume-playback__text">{{ resumeLabel }}</span>
      </button>
      <button
        class="resume-playback__dismiss"
        :aria-label="t('player.controls.resumeDismiss')"
        @click.stop="handleDismiss"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path :d="mdiClose" />
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, computed } from 'vue'
import { mdiPlay, mdiClose } from '@mdi/js'
import { useVideoI18n } from '../../i18n'
import { formatTime } from '../../utils/time'

const AUTO_HIDE_MS = 8_000

const props = defineProps<{
  savedPosition: number | null
  hasStarted: boolean
}>()

const emit = defineEmits<{
  resume: [position: number]
  dismiss: []
}>()

const { t } = useVideoI18n()

const visible = ref(false)
const dismissed = ref(false)
let hideTimer: ReturnType<typeof setTimeout> | null = null

const resumeLabel = computed(() =>
  t('player.controls.resumeAt', { time: formatTime(props.savedPosition ?? 0) }),
)

function showNotification(): void {
  visible.value = true
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    visible.value = false
    emit('dismiss')
  }, AUTO_HIDE_MS)
}

function handleResume(): void {
  if (hideTimer) clearTimeout(hideTimer)
  visible.value = false
  dismissed.value = true
  if (props.savedPosition !== null) {
    emit('resume', props.savedPosition)
  }
}

function handleDismiss(): void {
  if (hideTimer) clearTimeout(hideTimer)
  visible.value = false
  dismissed.value = true
  emit('dismiss')
}

// Show when playback starts and a saved position exists
watch(
  () => props.hasStarted && props.savedPosition !== null,
  (shouldShow) => {
    if (shouldShow && !dismissed.value) {
      showNotification()
    }
  },
  { immediate: true },
)

// Hide if savedPosition is cleared externally
watch(
  () => props.savedPosition,
  (pos) => {
    if (pos === null) {
      visible.value = false
      if (hideTimer) clearTimeout(hideTimer)
    }
  },
)

onUnmounted(() => {
  if (hideTimer) clearTimeout(hideTimer)
})
</script>

<style lang="scss" scoped>
.resume-playback {
  position: absolute;
  bottom: 80px;
  left: 16px;
  z-index: 60;
  display: flex;
  align-items: center;
  border-radius: 10px;
  background: var(--vp-backdrop-heavy);
  backdrop-filter: blur(12px);
  color: var(--vp-overlay-text-color, #ffffff);
  pointer-events: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);

  &__action {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border: none;
    border-radius: 10px 0 0 10px;
    background: transparent;
    color: inherit;
    font-size: 0.9375rem;
    font-weight: 600;
    font-family: inherit;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background: var(--vp-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: -2px;
    }
  }

  &__icon {
    flex-shrink: 0;
  }

  &__text {
    white-space: nowrap;
  }

  &__dismiss {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 12px;
    border: none;
    border-left: 1px solid var(--vp-border-subtle);
    border-radius: 0 10px 10px 0;
    background: transparent;
    color: var(--vp-text-tertiary);
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      background: var(--vp-surface-hover);
      color: var(--vp-text-primary);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: -2px;
    }
  }
}

[data-vp-theme='light'] .resume-playback {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.resume-playback-enter-active {
  animation: resume-in 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.resume-playback-leave-active {
  animation: resume-out 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes resume-in {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes resume-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(12px);
  }
}

@media (max-width: 768px) {
  .resume-playback {
    bottom: 64px;
    left: 12px;

    &__action {
      padding: 10px 12px;
      gap: 8px;
      font-size: 0.8125rem;
    }

    &__dismiss {
      padding: 10px 10px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .resume-playback-enter-active,
  .resume-playback-leave-active {
    animation-duration: 1ms;
  }
}
</style>
